"use client";

import type { PointerEvent, ReactNode, RefObject } from "react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib";
import {
	type SnapDragGesture,
	useSnapDragExpand,
} from "./use-snap-drag-expand";

function clamp01(t: number): number {
	return Math.min(1, Math.max(0, t));
}

/** Hermite smoothstep for placeholder ↔ content cross-fade */
function smoothstep01(t: number): number {
	const x = clamp01(t);
	return x * x * (3 - 2 * x);
}

/** Until `snapPx` is measured, approximate collapsed strip height for the drag hit zone */
const COLLAPSED_HIT_FALLBACK_PX = 96;
/** Subpixel / measurement jitter slack under `overflow-hidden` (focus rings, `translate-*`, etc.). */
const COLLAPSED_STRIP_SLACK_PX = 6;

const SM_BREAKPOINT_PX = 640;

export interface SnapDragSheetCollapsedBandPxPreset {
	/** Viewport `< sm` (Tailwind): collapsed strip + hit zone height */
	maxSm: number;
	/** Viewport `≥ sm`: collapsed strip + hit zone height */
	smMin: number;
}

function subscribeSmMin(cb: () => void): () => void {
	const mq = window.matchMedia(`(min-width: ${SM_BREAKPOINT_PX}px)`);
	mq.addEventListener("change", cb);
	return () => mq.removeEventListener("change", cb);
}

function smMinMatches(): boolean {
	return window.matchMedia(`(min-width: ${SM_BREAKPOINT_PX}px)`).matches;
}

export interface SnapDragSheetHandleProps {
	ariaLabel: string;
	snapPx: { c: number; e: number } | null;
	clipPx: number;
	/** `overlay` pins the rail (scenario hero). `inline` keeps it in layout flow (waitlist). */
	layout: "overlay" | "inline";
	/**
	 * Edge the handle sits on for `overlay` layout.
	 * `left` / `right` render a vertical grip (side drawers).
	 */
	position: "top" | "bottom" | "left" | "right";
	onPointerDown: (e: PointerEvent<HTMLDivElement>) => void;
	onPointerMove: (e: PointerEvent<HTMLDivElement>) => void;
	onPointerUp: (e: PointerEvent<HTMLDivElement>) => void;
	onDoubleClick: () => void;
	className?: string;
	/**
	 * Preset bar surface. Prefer this over raw {@link barClassName} for shared
	 * themes (e.g. translucent white on dark / cover gradients).
	 * @default "default"
	 */
	barTone?: SnapDragSheetHandleBarTone;
	/** Extra classes merged after {@link barTone} (escape hatch). */
	barClassName?: string;
	/**
	 * When false, the bar is decorative only — attach pointer handlers on a separate hit zone overlay.
	 */
	interactive?: boolean;
	/**
	 * Non-interactive muted handle (e.g. top drawer with nothing to expand).
	 * Overrides {@link interactive} when true.
	 */
	disabled?: boolean;
	/** `inline` layout only: hairline under the handle rail */
	handleSeparator?: boolean;
	/** Stable selector for Playwright / DOM smoke. */
	"data-testid"?: string;
}

/** Shared drag-handle bar surfaces for snap sheets / top drawers. */
export const SNAP_DRAG_SHEET_HANDLE_BAR_TONE_CLASS = {
	default:
		"bg-muted-foreground/40 shadow-sm dark:bg-white/30 dark:shadow-sm dark:shadow-black/25",
	/** Translucent white — dark / cover-gradient hosts (scenario hero, topic editor cover). */
	translucentWhite: "bg-white/45 shadow-sm",
} as const;

export type SnapDragSheetHandleBarTone =
	keyof typeof SNAP_DRAG_SHEET_HANDLE_BAR_TONE_CLASS;

export function SnapDragSheetHandle({
	ariaLabel,
	snapPx,
	clipPx,
	layout,
	position,
	onPointerDown,
	onPointerMove,
	onPointerUp,
	onDoubleClick,
	className,
	barTone = "default",
	barClassName,
	interactive = true,
	disabled = false,
	handleSeparator = true,
	"data-testid": dataTestId,
}: SnapDragSheetHandleProps) {
	const end = onPointerUp;
	const isInteractive = interactive && !disabled;
	const isVerticalGrip = position === "left" || position === "right";

	const interactiveProps = isInteractive
		? {
				role: "slider" as const,
				tabIndex: 0 as const,
				"aria-label": ariaLabel,
				"aria-valuemin": snapPx?.c ?? 0,
				"aria-valuemax": snapPx?.e ?? 0,
				"aria-valuenow": Math.round(clipPx),
				"aria-orientation": isVerticalGrip
					? ("vertical" as const)
					: ("horizontal" as const),
				onPointerDown,
				onPointerMove,
				onPointerUp: end,
				onPointerCancel: end,
				onLostPointerCapture: end,
				onDoubleClick,
			}
		: {
				"aria-disabled": true as const,
				"aria-label": ariaLabel,
			};

	const rail = (
		<div
			{...interactiveProps}
			className={cn(
				"flex justify-center outline-none",
				isVerticalGrip
					? cn(
							"h-full max-h-[14rem] flex-col items-center",
							layout === "inline" ? "px-0" : "px-1.5",
						)
					: cn(
							"w-full max-w-[14rem]",
							layout === "inline" ? "py-0.5" : "py-1.5",
						),
				isInteractive &&
					"cursor-grab touch-none active:cursor-grabbing focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				!isInteractive && !disabled && "pointer-events-none",
				disabled && "cursor-not-allowed opacity-40",
				layout === "overlay" && isInteractive && "pointer-events-auto",
			)}
		>
			<span
				aria-hidden
				data-bar-tone={barTone}
				className={cn(
					"block rounded-full",
					isVerticalGrip ? "h-10 w-1.5" : "h-1.5 w-10",
					SNAP_DRAG_SHEET_HANDLE_BAR_TONE_CLASS[barTone],
					barClassName,
				)}
			/>
		</div>
	);

	if (layout === "inline") {
		return (
			<div
				data-testid={dataTestId}
				className={cn(
					"flex shrink-0 justify-center bg-transparent",
					isVerticalGrip ? "h-full flex-col" : "w-full",
					handleSeparator &&
						(isVerticalGrip
							? "border-border/40 border-r dark:border-white/15"
							: "border-border/40 border-b dark:border-white/15"),
					!isInteractive && !disabled && "pointer-events-none",
					disabled && "cursor-not-allowed",
					className,
				)}
			>
				{rail}
			</div>
		);
	}

	return (
		<div
			data-testid={dataTestId}
			className={cn(
				"pointer-events-none absolute z-20 flex justify-center @[830px]:px-3",
				isVerticalGrip
					? cn(
							"top-0 bottom-0 flex-col items-center @[830px]:px-0 @[830px]:py-3",
							position === "left"
								? "left-0 pl-0.5"
								: "right-0 pr-0.5",
						)
					: cn(
							"left-0 right-0",
							position === "bottom"
								? "bottom-0 pb-0.5"
								: "top-0 pt-0.5",
						),
				className,
			)}
		>
			{rail}
		</div>
	);
}

export interface SnapDragSheetProps {
	gesture: SnapDragGesture;
	handleAriaLabel: string;
	collapsed: ReactNode;
	expanded: ReactNode;
	defaultExpanded?: boolean;
	className?: string;
	handleClassName?: string;
	/** @see {@link SnapDragSheetHandleProps.barTone} */
	handleBarTone?: SnapDragSheetHandleBarTone;
	handleBarClassName?: string;
	clipClassName?: string;
	/**
	 * Render only `expanded` (e.g. desktop) so the same subtree stays mounted across breakpoints.
	 */
	passthrough?: boolean;
	/**
	 * When set, the drag hit zone is portaled here (`absolute top/inset-x` vs that container).
	 * Use for full-bleed handles wider than the sheet host (e.g. waitlist white panel).
	 */
	dragHitZoneMountRef?: RefObject<HTMLElement | null>;
	/**
	 * Fixed collapsed strip height by breakpoint (`snapPx.c` + hit zone) instead of measuring
	 * handle + collapsed row (e.g. waitlist tuned heights).
	 */
	collapsedBandPxPreset?: SnapDragSheetCollapsedBandPxPreset;
	/** `inline` layout: hairline under the handle rail */
	handleSeparator?: boolean;
	/** Extra classes for the drag hit strip (e.g. `top-6 sm:top-8` to match root `mt-*`) */
	dragHitZoneClassName?: string;
	/** See {@link useSnapDragExpand} `snapCommitThreshold`. */
	snapCommitThreshold?: number;
}

type SnapDragSheetInteractiveProps = Omit<SnapDragSheetProps, "passthrough">;

function SnapDragSheetInteractive({
	gesture,
	handleAriaLabel,
	collapsed,
	expanded,
	defaultExpanded = true,
	className,
	handleClassName,
	handleBarTone,
	handleBarClassName,
	clipClassName,
	dragHitZoneMountRef,
	collapsedBandPxPreset,
	handleSeparator = true,
	dragHitZoneClassName,
	snapCommitThreshold = 0.5,
}: SnapDragSheetInteractiveProps) {
	const hostRef = useRef<HTMLDivElement>(null);
	const handleRailRef = useRef<HTMLDivElement>(null);
	const collapsedProbeRef = useRef<HTMLDivElement>(null);
	const expandedBodyRef = useRef<HTMLDivElement>(null);
	const prevClipPxRef = useRef<number | undefined>(undefined);
	const prevExpandTRef = useRef<number>(defaultExpanded ? 1 : 0);

	const [snapPx, setSnapPx] = useState<{ c: number; e: number } | null>(null);
	const measureRef = useRef(() => {});

	const {
		clipPx,
		expandT,
		maskDragging,
		setExpandT,
		onHandlePointerDown,
		onHandlePointerMove,
		onHandlePointerUp,
		onHandlePointerCancel,
		onLostPointerCapture,
	} = useSnapDragExpand({
		snapPx,
		gesture,
		initialExpandT: defaultExpanded ? 1 : 0,
		fallbackClipPx: defaultExpanded
			? Math.max(280, snapPx?.e ?? 280)
			: collapsedBandPxPreset != null
				? collapsedBandPxPreset.maxSm + 56
				: Math.max(112, COLLAPSED_HIT_FALLBACK_PX),
		snapCommitThreshold,
		onAfterSnap: () => {
			measureRef.current();
		},
		getTransitionKillTargets: () => [hostRef.current],
	});

	const measure = useCallback(() => {
		const handleH = handleRailRef.current?.offsetHeight ?? 32;
		const colH = collapsedProbeRef.current?.offsetHeight ?? 36;
		const expH = expandedBodyRef.current?.offsetHeight ?? 200;
		const measuredStrip = Math.round(handleH + colH);

		const handleEl = handleRailRef.current;
		const collapsedEl = collapsedProbeRef.current;
		let paintedCollapsedStrip = measuredStrip;
		if (handleEl && collapsedEl) {
			const handlePaintEl =
				handleEl.firstElementChild instanceof HTMLElement
					? handleEl.firstElementChild
					: handleEl;
			const collapsedPaintEl =
				collapsedEl.firstElementChild instanceof HTMLElement
					? collapsedEl.firstElementChild
					: collapsedEl;
			const a = handlePaintEl.getBoundingClientRect();
			const b = collapsedPaintEl.getBoundingClientRect();
			paintedCollapsedStrip = Math.max(
				measuredStrip,
				Math.ceil(
					Math.max(a.bottom, b.bottom) - Math.min(a.top, b.top),
				),
			);
		}

		const presetBandPx =
			collapsedBandPxPreset != null
				? smMinMatches()
					? collapsedBandPxPreset.smMin
					: collapsedBandPxPreset.maxSm
				: null;
		// Preset sets target strip height; never clip below intrinsic handle + collapsed row
		// (resize/font changes can exceed the preset — overflow-hidden would hide handle/hint).
		// `getBoundingClientRect` accounts for transforms (`translate-*`) which layout heights omit.
		const cBase =
			presetBandPx != null
				? Math.max(presetBandPx, paintedCollapsedStrip)
				: paintedCollapsedStrip;
		const c = Math.round(cBase + COLLAPSED_STRIP_SLACK_PX);
		const e = Math.round(Math.max(c + 48, handleH + expH));
		setSnapPx((prev) => {
			if (prev && prev.c === c && prev.e === e) {
				return prev;
			}
			return { c, e };
		});
	}, [collapsedBandPxPreset]);

	measureRef.current = measure;

	useLayoutEffect(() => {
		measure();
		const expandedEl = expandedBodyRef.current;
		const collapsedEl = collapsedProbeRef.current;
		const handleEl = handleRailRef.current;
		const ro = new ResizeObserver(() => measure());
		if (expandedEl) {
			ro.observe(expandedEl);
		}
		if (collapsedEl) {
			ro.observe(collapsedEl);
		}
		if (handleEl) {
			ro.observe(handleEl);
		}
		function onWinResize() {
			measure();
		}
		window.addEventListener("resize", onWinResize);
		return () => {
			ro.disconnect();
			window.removeEventListener("resize", onWinResize);
		};
	}, [measure, collapsed, expanded]);

	useLayoutEffect(() => {
		if (collapsedBandPxPreset == null) {
			return;
		}
		return subscribeSmMin(() => {
			measure();
		});
	}, [collapsedBandPxPreset, measure]);

	/**
	 * When only remeasurement changes `clipPx` (same `expandT`), CSS height tween can pass through
	 * heights shorter than painted collapsed chrome. Skip transition for those updates; keep tween
	 * when `expandT` moves (user expand/collapse). Includes fallback→first `snapPx` clip changes.
	 */
	useLayoutEffect(() => {
		const hostEl = hostRef.current;
		const prevClip = prevClipPxRef.current;
		const prevEx = prevExpandTRef.current;

		const clipChanged = prevClip !== undefined && prevClip !== clipPx;
		const expandUnchanged = expandT === prevEx;

		prevClipPxRef.current = clipPx;
		prevExpandTRef.current = expandT;

		if (
			maskDragging ||
			hostEl == null ||
			!clipChanged ||
			!expandUnchanged
		) {
			return;
		}

		hostEl.style.setProperty("transition", "none");
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				hostEl.style.removeProperty("transition");
			});
		});
	}, [clipPx, expandT, maskDragging]);

	const fadeExpanded = smoothstep01(expandT);

	const ctxStyle = useMemo(
		() => ({
			height: `${clipPx}px`,
			willChange: maskDragging ? ("height" as const) : undefined,
		}),
		[clipPx, maskDragging],
	);

	const onDoubleClick = useCallback(() => {
		setExpandT((t) => (t > 0.5 ? 0 : 1));
		queueMicrotask(measure);
	}, [measure, setExpandT]);

	const collapsedBandPx =
		snapPx?.c ??
		(collapsedBandPxPreset != null
			? collapsedBandPxPreset.maxSm
			: COLLAPSED_HIT_FALLBACK_PX);

	const [dragHitZonePortalEl, setDragHitZonePortalEl] =
		useState<HTMLElement | null>(null);

	useLayoutEffect(() => {
		if (!dragHitZoneMountRef) {
			setDragHitZonePortalEl(null);
			return;
		}
		function syncPortalMount(): void {
			const next = dragHitZoneMountRef?.current ?? null;
			setDragHitZonePortalEl((prev) => (prev === next ? prev : next));
		}
		syncPortalMount();
		// Parent mount node can attach immediately after this subtree commits; retry once.
		queueMicrotask(syncPortalMount);
	}, [dragHitZoneMountRef]);

	const dragHitZoneNode = (
		<div
			role="slider"
			tabIndex={0}
			aria-label={handleAriaLabel}
			aria-valuemin={snapPx?.c ?? 0}
			aria-valuemax={snapPx?.e ?? Math.round(clipPx)}
			aria-valuenow={Math.round(clipPx)}
			className={cn(
				"absolute inset-x-0 top-0 z-[50] cursor-grab touch-none bg-transparent outline-none select-none active:cursor-grabbing",
				"focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				dragHitZoneClassName,
			)}
			style={{ height: `${collapsedBandPx}px` }}
			onPointerDown={onHandlePointerDown}
			onPointerMove={onHandlePointerMove}
			onPointerUp={onHandlePointerUp}
			onPointerCancel={onHandlePointerCancel}
			onLostPointerCapture={onLostPointerCapture}
			onDoubleClick={onDoubleClick}
		/>
	);

	const renderHitZoneInsideHost = dragHitZoneMountRef == null;

	return (
		<div className={cn("w-full", className)}>
			<div
				ref={hostRef}
				className={cn(
					"relative flex w-full shrink-0 flex-col overflow-hidden rounded-t-xl",
					clipClassName,
					!maskDragging &&
						"transition-[height] duration-200 ease-out motion-reduce:transition-none",
					maskDragging && "!transition-none",
				)}
				style={ctxStyle}
			>
				{renderHitZoneInsideHost ? dragHitZoneNode : null}
				<div ref={handleRailRef} className="relative z-10 shrink-0">
					<SnapDragSheetHandle
						ariaLabel={handleAriaLabel}
						snapPx={snapPx}
						clipPx={clipPx}
						layout="inline"
						position="top"
						onPointerDown={onHandlePointerDown}
						onPointerMove={onHandlePointerMove}
						onPointerUp={onHandlePointerUp}
						onDoubleClick={onDoubleClick}
						className={handleClassName}
						barTone={handleBarTone}
						barClassName={handleBarClassName}
						handleSeparator={handleSeparator}
						interactive={false}
					/>
				</div>

				<div className="relative flex min-h-0 flex-1 flex-col overflow-hidden px-3 sm:px-4">
					{/*
					  Expanded content must not participate in row sizing with collapsed chrome:
					  a shared grid row grows to the full form height, so overflow clipping hides
					  the handle/hint and shows only the top of the expanded subtree (e.g. logo).
					*/}
					<div
						ref={collapsedProbeRef}
						className={cn(
							"relative z-10 flex w-full max-w-full shrink-0 flex-col items-center justify-center px-3 py-2 transition-opacity duration-200 ease-out motion-reduce:transition-none",
							maskDragging && "!transition-none",
							expandT > 0.65 && "pointer-events-none",
						)}
						style={{ opacity: 1 - fadeExpanded }}
					>
						{collapsed}
					</div>
					<div
						className={cn(
							"absolute inset-x-0 top-0 z-0 min-h-0 w-full transition-opacity duration-200 ease-out motion-reduce:transition-none",
							maskDragging && "!transition-none",
							expandT < 0.35 && "pointer-events-none",
						)}
						style={{
							opacity: fadeExpanded,
							visibility: expandT < 0.02 ? "hidden" : undefined,
						}}
						aria-hidden={expandT < 0.02}
					>
						<div ref={expandedBodyRef} className="w-full">
							{expanded}
						</div>
					</div>
				</div>
			</div>
			{dragHitZonePortalEl != null && dragHitZoneMountRef != null
				? createPortal(dragHitZoneNode, dragHitZonePortalEl)
				: null}
		</div>
	);
}

export function SnapDragSheet({
	passthrough = false,
	className,
	expanded,
	dragHitZoneMountRef,
	handleSeparator,
	collapsedBandPxPreset,
	dragHitZoneClassName,
	...interactiveProps
}: SnapDragSheetProps) {
	if (passthrough) {
		return <div className={cn("w-full", className)}>{expanded}</div>;
	}
	return (
		<SnapDragSheetInteractive
			className={className}
			collapsedBandPxPreset={collapsedBandPxPreset}
			dragHitZoneClassName={dragHitZoneClassName}
			dragHitZoneMountRef={dragHitZoneMountRef}
			expanded={expanded}
			handleSeparator={handleSeparator}
			{...interactiveProps}
		/>
	);
}

export type {
	SnapDragGesture,
	UseSnapDragExpandOptions,
	UseSnapDragExpandResult,
} from "./use-snap-drag-expand";
export {
	resolveSnapExpandT,
	useSnapDragExpand,
} from "./use-snap-drag-expand";
